export const QUICK_REVISION_PROMPTS = [
  "Shorter",
  "Less hype",
  "Stronger CTA",
  "More personal",
  "More technical",
];

function asText(value) {
  return typeof value === "string" ? value : "";
}

function cleanInstruction(instruction) {
  return asText(instruction).trim() || "Improve this post";
}

function createId() {
  return `rev_${Math.random().toString(36).slice(2, 8)}`;
}

function removeHype(text) {
  return asText(text)
    .replace(/\bmassive\b/gi, "meaningful")
    .replace(/\bgame changer\b/gi, "practical shift")
    .replace(/\bgame-changing\b/gi, "useful")
    .replace(/!+/g, ".");
}

function shortenText(text) {
  const paragraphs = asText(text).split(/\n\n+/).filter(Boolean);
  if (paragraphs.length > 1) return paragraphs[0];
  const sentences = asText(text).split(/(?<=[.!?])\s+/).filter(Boolean);
  return sentences.slice(0, Math.max(1, Math.min(2, sentences.length))).join(" ");
}

function deriveTopic(asset) {
  const title = asText(asset?.title).replace(/^(LinkedIn|X Thread|Instagram|Facebook|Threads|Bluesky|Shorts)\s+(?:-|\u2013|\u2014)\s+/i, "");
  if (title) return title;
  return asText(asset?.hook) || "this idea";
}

function reviseCopy(asset, instruction) {
  const lower = instruction.toLowerCase();
  let hook = asText(asset.hook);
  let body = asText(asset.body);
  let cta = asText(asset.cta);

  if (lower.includes("less hype")) {
    hook = removeHype(hook);
    body = removeHype(body);
    cta = removeHype(cta);
  }

  if (lower.includes("short")) {
    body = shortenText(body);
  }

  if (lower.includes("personal")) {
    body = `Here is how I think about it in practice:\n\n${body}`;
  }

  if (lower.includes("technical")) {
    body = `${body.trim()}\n\nTechnical note: the useful part is not the prompt. It is the repeatable workflow, clear inputs, and a review gate before anything ships.`;
  }

  if (lower.includes("cta")) {
    cta = "If you want the working checklist, send me a DM and I will share the exact setup.";
  }

  if (hook === asset.hook && body === asset.body && cta === asset.cta) {
    body = `${body.trim()}\n\nRevision note applied: ${instruction}`;
  }

  return { hook, body, cta };
}

function regenerateCopy(asset, instruction) {
  const lower = instruction.toLowerCase();
  const topic = deriveTopic(asset);
  const hook = lower.includes("personal")
    ? `What I keep learning about ${topic}`
    : `A cleaner way to explain ${topic}`;

  let body = [
    `Most people try to fix this with another prompt. I think that misses the real lever.`,
    `The stronger move is to turn the work into a repeatable workflow: clear input, specific skill, visible review, and platform-ready output.`,
    `That makes the system easier to trust because you can see what changed before anything gets approved.`,
  ].join("\n\n");

  if (lower.includes("technical")) {
    body += "\n\nTechnical note: the workflow contract matters because it gives the agent boundaries, expected output shape, and a review state.";
  }

  if (lower.includes("personal")) {
    body = `I have had to learn this the practical way.\n\n${body}`;
  }

  const cta = lower.includes("cta")
    ? "Want the checklist I use for this? Send me a DM."
    : asText(asset.cta) || "Save this if you are building the same kind of system.";

  return { hook, body, cta };
}

export function createRevisionPatch(asset, options = {}) {
  const instruction = cleanInstruction(options.instruction);
  const mode = options.mode === "regenerate" ? "regenerate" : "revise";
  const source = options.source || "manual";
  const next = mode === "regenerate"
    ? regenerateCopy(asset, instruction)
    : reviseCopy(asset, instruction);
  const hashtags = Array.isArray(asset?.hashtags) ? asset.hashtags : [];
  const charCount = [next.hook, next.body, next.cta, hashtags.join(" ")].filter(Boolean).join("\n").length;
  const before = {
    hook: asText(asset?.hook),
    body: asText(asset?.body),
    cta: asText(asset?.cta),
  };
  const entry = {
    id: createId(),
    created_at: new Date().toISOString(),
    instruction,
    mode,
    source,
    before,
    after: next,
    summary: mode === "regenerate" ? "Generated a fresh variation" : "Applied requested revision",
  };

  return {
    hook: next.hook,
    body: next.body,
    cta: next.cta,
    char_count: charCount,
    status: "needs_review",
    revision_history: [...(asset?.revision_history || []), entry],
  };
}
